/**
 * The signed-in session: acquiring a token, keeping it fresh, and getting it back for a
 * request.
 *
 * Where tokens live is the decision worth stating. The access token is held in memory only,
 * so it is gone on reload and cannot be read out of storage by anything running on the page.
 * The refresh token goes in sessionStorage, which is scoped to the tab and cleared when it
 * closes - not localStorage, which survives indefinitely on a shared drafting workstation.
 *
 * That is a deliberate middle position. In-memory only would mean a full redirect through
 * Entra on every reload of a tool somebody keeps open all day; localStorage would leave a
 * 24-hour credential on a machine other people use.
 */

import {
  authorizeUrl,
  challengeFromVerifier,
  createState,
  createVerifier,
  exchangeCode,
  readClaims,
  refreshTokens,
  type AccessTokenClaims,
  type EntraConfig,
} from "./pkce.ts";

/** Keys are tab-scoped, and only the two that have to survive the redirect. */
const VERIFIER_KEY = "plan-forge.pkce.verifier";
const STATE_KEY = "plan-forge.pkce.state";
const RETURN_KEY = "plan-forge.pkce.return";
const REFRESH_KEY = "plan-forge.refresh";

/**
 * Refresh this far before expiry rather than on it. A request that leaves with 20 seconds of
 * validity and arrives with none fails for a reason nobody can reproduce.
 */
const REFRESH_MARGIN_SECONDS = 120;

export interface Session {
  readonly accessToken: string;
  readonly expiresAt: number;
  readonly claims: AccessTokenClaims | null;
}

export interface SignedInUser {
  readonly name: string | null;
  readonly username: string | null;
  readonly roles: readonly string[];
}

export function userFrom(session: Session): SignedInUser {
  return {
    name: session.claims?.name ?? null,
    username: session.claims?.preferred_username ?? null,
    roles: session.claims?.roles ?? [],
  };
}

/**
 * Starts a sign-in by leaving the page.
 *
 * The current URL is remembered so somebody who arrives at a deep link is returned there
 * rather than to the root, which is the difference between a redirect being invisible and
 * being a small loss of work.
 */
export async function beginSignIn(config: EntraConfig): Promise<never> {
  const verifier = createVerifier();
  const state = createState();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, window.location.href);

  const challenge = await challengeFromVerifier(verifier);
  window.location.assign(authorizeUrl(config, state, challenge));
  // The assignment above navigates away; this only satisfies the never return type.
  return await new Promise<never>(() => {});
}

export interface RedirectResult {
  readonly session: Session;
  /** Where the user was before signing in. */
  readonly returnTo: string;
}

/**
 * Completes a sign-in if the current URL is a callback, and returns null if it is not.
 *
 * The state check is not ceremony. Without it a crafted callback URL could plant an
 * attacker's authorization code in this tab, and the browser would exchange it for a token
 * bound to their account - which is a real, named attack rather than a theoretical one.
 */
export async function completeSignIn(config: EntraConfig): Promise<RedirectResult | null> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (error !== null) {
    clearFlow();
    throw new Error(params.get("error_description") ?? error);
  }

  const code = params.get("code");
  const state = params.get("state");
  if (code === null || state === null) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? "/";
  clearFlow();

  if (expectedState === null || verifier === null) {
    throw new Error(
      "This sign-in did not start in this tab. Start again from the application rather than from a link.",
    );
  }
  if (state !== expectedState) {
    throw new Error("The sign-in state did not match. Start again from the application.");
  }

  const tokens = await exchangeCode(config, code, verifier);
  if (tokens.refresh_token !== undefined) {
    sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  }
  return { session: sessionFrom(tokens.access_token, tokens.expires_in), returnTo };
}

/**
 * Restores a session without user interaction, or returns null.
 *
 * This is what makes a reload cheap. A refresh token that Entra has revoked - conditional
 * access changed, the user was unassigned from the app role - fails here, and failing quietly
 * to a sign-in prompt is the right behaviour: the alternative is an error about a token the
 * person did not know existed.
 */
export async function restoreSession(config: EntraConfig): Promise<Session | null> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (refreshToken === null) return null;
  try {
    const tokens = await refreshTokens(config, refreshToken);
    if (tokens.refresh_token !== undefined) {
      sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    }
    return sessionFrom(tokens.access_token, tokens.expires_in);
  } catch {
    sessionStorage.removeItem(REFRESH_KEY);
    return null;
  }
}

/**
 * The token to put on a request, refreshed first if it is close to expiring.
 *
 * Returns the new session alongside the token so the caller can hold it; this deliberately
 * keeps no module-level mutable state, which is what makes it testable and what stops two
 * parts of an application disagreeing about who is signed in.
 */
export async function tokenFor(
  config: EntraConfig,
  session: Session,
  now: number = Date.now(),
): Promise<{ token: string; session: Session }> {
  if (session.expiresAt - now > REFRESH_MARGIN_SECONDS * 1000) {
    return { token: session.accessToken, session };
  }
  const refreshed = await restoreSession(config);
  if (refreshed === null) {
    throw new Error("The session expired. Sign in again.");
  }
  return { token: refreshed.accessToken, session: refreshed };
}

export function signOut(): void {
  sessionStorage.removeItem(REFRESH_KEY);
  clearFlow();
}

function clearFlow(): void {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);
}

function sessionFrom(accessToken: string, expiresInSeconds: number): Session {
  return {
    accessToken,
    expiresAt: Date.now() + expiresInSeconds * 1000,
    claims: readClaims(accessToken),
  };
}
